// The AI API and AI tools (14 October 2026): the platform's AI models, charged to the business's credits at its
// plan's rate. `services/flows-llm`, reached through the gateway at `/v1/ai/**`. An API key needs the `ai` scope;
// the console calls the same routes with the signed-in person's session.
//
// Speech, transcription and pictures (phase 2, 14 October 2026) are priced per 1 000 characters, per minute of audio
// (by the second, at least 15) and per picture.
//
// Every call holds the most it can cost first and is charged what the model really used: 402
// `INSUFFICIENT_CREDITS` when the credit cannot cover it, 429 `AI_DAILY_LIMIT_REACHED` when an API key's daily AI
// limit would be passed. Agents may read models, skills and history but not run anything (403).

import { http } from './_core/request.js';
import type { Envelope } from './_core/envelope.js';

/** 402: the wallet cannot cover the most the call could cost. `required_minor` and `available_minor` say by how much. */
export const INSUFFICIENT_CREDITS = 'INSUFFICIENT_CREDITS';
/** 429: this API key's daily AI limit would be passed. */
export const AI_DAILY_LIMIT_REACHED = 'AI_DAILY_LIMIT_REACHED';
/** 400: no model by that code is on offer. */
export const AI_MODEL_UNKNOWN = 'AI_MODEL_UNKNOWN';
/** 400: the chosen model cannot read pictures. */
export const AI_MODEL_NO_IMAGES = 'AI_MODEL_NO_IMAGES';
/** 502: the model could not answer; nothing was charged. */
export const AI_PROVIDER_ERROR = 'AI_PROVIDER_ERROR';
/** 503: no model of that kind is switched on yet. */
export const AI_MODEL_UNAVAILABLE = 'AI_MODEL_UNAVAILABLE';
/** A transcription could not be finished; its hold was given back. Read at GET /v1/ai/requests/{id} as `error`. */
export const AI_TRANSCRIPTION_FAILED = 'AI_TRANSCRIPTION_FAILED';

export type AiModelKind = 'text' | 'speech' | 'transcribe' | 'image';
export type AiFormat = 'text' | 'markdown' | 'html' | 'json';

export interface AiPrice {
  sku: string;
  /** `10k_tokens`, `1k_chars`, `minute` or `image`. */
  unit: string | null;
  /** Your price per unit, in minor units, at your plan's rate. */
  rate_minor: number;
}

export interface AiModel {
  code: string;
  kind: AiModelKind;
  name: string;
  description?: string | null;
  provider: string;
  accepts_images: boolean;
  max_output_tokens: number;
  /** It runs outside South Africa. */
  outside_south_africa: boolean;
  currency: string;
  /** A text model has two: what it reads and what it writes. */
  prices: AiPrice[];
  /** False: it has no price yet and cannot be used. */
  priced: boolean;
}

/** A picture: base64 bytes (a data: URL is accepted) and its media type. */
export interface AiImageInput {
  data: string;
  media_type: 'image/png' | 'image/jpeg' | 'image/gif' | 'image/webp';
}

export interface AiMessageInput {
  role: 'user' | 'assistant';
  content?: string | null;
  images?: AiImageInput[] | null;
}

export interface AiTextRequest {
  messages: AiMessageInput[];
  model?: string | null;
  system?: string | null;
  /** The longest answer, in tokens. Defaults to 1 000; capped by the model. */
  max_tokens?: number | null;
  /** 0 for the most predictable answer, up to 1 for the most varied. Claude models choose their own and ignore it. */
  temperature?: number | null;
  format?: AiFormat | null;
}

export interface AiVisionRequest {
  image: AiImageInput;
  /** `describe` (the default), `text` (read every word) or `question`. */
  task?: 'describe' | 'text' | 'question' | null;
  question?: string | null;
  model?: string | null;
  format?: AiFormat | null;
  max_tokens?: number | null;
}

export interface AiAnswerRequest {
  question: string;
  /** Only these knowledge base documents. */
  document_ids?: string[] | null;
  model?: string | null;
  format?: Exclude<AiFormat, 'json'> | null;
  max_tokens?: number | null;
}

export interface AiRunRequest {
  /** A skill code: summarise, write, rewrite, translate, reply, extract, classify, or your own. */
  skill: string;
  inputs?: Record<string, string> | null;
  model?: string | null;
  format?: AiFormat | null;
}

export interface AiSource {
  document_id: string;
  name: string;
  page: number;
  excerpt: string;
}

export interface AiTextResult {
  request_id: string;
  text: string;
  /** The answer as JSON, for format json and for the extract and classify skills. */
  json?: unknown;
  model: string;
  model_name: string;
  usage: { input_tokens: number; output_tokens: number };
  /** What the call cost, in minor units. */
  charged_minor: number;
  currency: string;
  outside_south_africa: boolean;
  /** The platform answered with its stand-in (no AI configured); nothing real was asked. */
  stub: boolean;
  sources?: AiSource[] | null;
  /** answer: your documents do not say. */
  unsure?: boolean | null;
}

export interface AiSkillInput {
  name: string;
  required: boolean;
  description: string;
  choices?: string[] | null;
}

export interface AiSkill {
  code: string;
  name: string;
  description: string;
  built_in: boolean;
  inputs: AiSkillInput[];
  model?: string | null;
  json: boolean;
  /** Your own skills only. */
  id?: string | null;
  instructions?: string | null;
  context?: string | null;
  tone?: string | null;
  temperature?: number | null;
  max_tokens?: number | null;
  use_knowledge_base?: boolean;
}

export interface SaveAiSkillRequest {
  /** 3 to 40 lower-case letters, digits and hyphens. */
  code: string;
  name: string;
  instructions: string;
  description?: string | null;
  context?: string | null;
  tone?: string | null;
  model?: string | null;
  temperature?: number | null;
  max_tokens?: number | null;
  use_knowledge_base?: boolean | null;
}

export interface AiEstimateRequest {
  model?: string | null;
  input_chars?: number | null;
  images?: number | null;
  max_tokens?: number | null;
}

export interface AiEstimate {
  model: string;
  max_charge_minor: number;
  currency: string;
  available_minor: number;
  daily_limit_minor?: number | null;
  spent_today_minor?: number | null;
  prices: AiPrice[];
}

export interface AiRequestRow {
  id: string;
  tool: 'text' | 'vision' | 'answer' | 'skill' | 'speech' | 'transcribe' | 'image';
  model: string;
  skill?: string | null;
  status: 'held' | 'done' | 'failed' | 'refused';
  charged_minor: number;
  input_tokens: number;
  output_tokens: number;
  units: number;
  api_key_name?: string | null;
  by_api_key: boolean;
  preview?: string | null;
  error?: string | null;
  created_at: string;
}

export function listModels(): Promise<Envelope<AiModel[]>> {
  return http.get<Envelope<AiModel[]>>('/v1/ai/models');
}

export function text(req: AiTextRequest): Promise<Envelope<AiTextResult>> {
  return http.post<Envelope<AiTextResult>>('/v1/ai/text', { body: req });
}

export function vision(req: AiVisionRequest): Promise<Envelope<AiTextResult>> {
  return http.post<Envelope<AiTextResult>>('/v1/ai/vision', { body: req });
}

export function answer(req: AiAnswerRequest): Promise<Envelope<AiTextResult>> {
  return http.post<Envelope<AiTextResult>>('/v1/ai/answer', { body: req });
}

export function listSkills(): Promise<Envelope<AiSkill[]>> {
  return http.get<Envelope<AiSkill[]>>('/v1/ai/skills');
}

export function runSkill(req: AiRunRequest): Promise<Envelope<AiTextResult>> {
  return http.post<Envelope<AiTextResult>>('/v1/ai/run', { body: req });
}

export function createSkill(req: SaveAiSkillRequest): Promise<Envelope<AiSkill>> {
  return http.post<Envelope<AiSkill>>('/v1/ai/skills', { body: req });
}

export function updateSkill(id: string, req: SaveAiSkillRequest): Promise<Envelope<AiSkill>> {
  return http.put<Envelope<AiSkill>>(`/v1/ai/skills/${encodeURIComponent(id)}`, { body: req });
}

export function deleteSkill(id: string): Promise<void> {
  return http.delete<void>(`/v1/ai/skills/${encodeURIComponent(id)}`);
}

export function estimate(req: AiEstimateRequest): Promise<Envelope<AiEstimate>> {
  return http.post<Envelope<AiEstimate>>('/v1/ai/estimate', { body: req });
}

export function listRequests(limit?: number): Promise<Envelope<AiRequestRow[]>> {
  return http.get<Envelope<AiRequestRow[]>>('/v1/ai/requests', { query: limit ? { limit } : undefined });
}

// ── speech, transcription and pictures ────────────────────────────────────────

export interface AiVoice {
  /** Send this as `voice`, e.g. `Ayanda`. */
  id: string;
  name: string;
  gender: string;
  language_code: string;
  language_name: string;
}

export interface AiSpeechRequest {
  /** Up to 3 000 characters. */
  text: string;
  /** A voice id from `listVoices`. */
  voice: string;
  model?: string | null;
  /** `mp3` (the default) or `ogg`. */
  format?: 'mp3' | 'ogg' | null;
  /** Also save it to your Files, with a link. */
  save?: boolean | null;
}

export interface AiSpeechResult {
  request_id: string;
  /** The audio as base64. */
  audio: string;
  media_type: string;
  characters: number;
  charged_minor: number;
  currency: string;
  media_id?: string | null;
  url?: string | null;
  stub: boolean;
}

export interface AiTranscribeRequest {
  /** A recording in your Files. Send this or `audio`. */
  media_id?: string | null;
  /** Base64 WAV, MP3, OGG, FLAC or M4A (a data: URL is accepted), up to 20 MB; upload a larger one to Files. */
  audio?: { data: string; media_type?: string | null } | null;
  /** e.g. `en-ZA`, `af-ZA`, `zu-ZA`. Omit and the language is worked out. */
  language?: string | null;
  model?: string | null;
}

export interface AiTranscription {
  request_id: string;
  /** `running` (HTTP 202: read it again at `getRequest`), `done` or `failed`. */
  status: 'running' | 'done' | 'failed';
  text?: string | null;
  language?: string | null;
  seconds: number;
  /** Charged when it is done; nothing while it runs or when it fails. */
  charged_minor: number;
  currency: string;
  stub: boolean;
}

export interface AiImagesRequest {
  /** What to show, up to 1 024 characters. */
  prompt: string;
  /** What to leave out. */
  avoid?: string | null;
  /** 1 to 4; defaults to 1. */
  count?: number | null;
  /** `square` 1024×1024 (the default), `landscape` 1280×720 or `portrait` 720×1280. */
  size?: 'square' | 'landscape' | 'portrait' | null;
  model?: string | null;
  /** Save them to your Files and answer with links instead of the pictures. */
  save?: boolean | null;
}

export interface AiPicture {
  media_type: string;
  /** Base64 PNG, when not saved. */
  data?: string | null;
  media_id?: string | null;
  url?: string | null;
}

export interface AiImagesResult {
  request_id: string;
  images: AiPicture[];
  charged_minor: number;
  currency: string;
  outside_south_africa: boolean;
  stub: boolean;
}

export interface AiRequestDetail extends AiRequestRow {
  /** A transcription's text. */
  result?: string | null;
  /** Files the call saved. */
  media_ids: string[];
  completed_at?: string | null;
}

/** The voices a speech model can speak in, South African English first. */
export function listVoices(model?: string): Promise<Envelope<AiVoice[]>> {
  return http.get<Envelope<AiVoice[]>>('/v1/ai/voices', { query: model ? { model } : undefined });
}

/** Text to speech, charged per 1 000 characters. */
export function speech(req: AiSpeechRequest): Promise<Envelope<AiSpeechResult>> {
  return http.post<Envelope<AiSpeechResult>>('/v1/ai/speech', { body: req });
}

/**
 * Speech to text, charged per minute by the second (at least 15). A long recording comes back `running`: poll
 * `getRequest(request_id)` until its status is `done` (the text is in `result`) or `failed`.
 */
export function transcribe(req: AiTranscribeRequest): Promise<Envelope<AiTranscription>> {
  return http.post<Envelope<AiTranscription>>('/v1/ai/transcribe', { body: req });
}

/** Pictures from a description, charged per picture. */
export function images(req: AiImagesRequest): Promise<Envelope<AiImagesResult>> {
  return http.post<Envelope<AiImagesResult>>('/v1/ai/images', { body: req });
}

/** One AI call: its status, its cost, a transcription's text and the files it saved. */
export function getRequest(id: string): Promise<Envelope<AiRequestDetail>> {
  return http.get<Envelope<AiRequestDetail>>(`/v1/ai/requests/${encodeURIComponent(id)}`);
}
